import { useState } from "react";
import { Box, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, Dialog } from "@platform/design-system";

import {
  buildCreditsLabelDiff,
  buildToggleCreditsHiddenDiff,
} from "../buildPatchDiff";

import type { FinancialsGridContext, FinancialsRow } from "../types";
import type { ICellRendererParams } from "ag-grid-community";

export const CreditsLabelCellRenderer = ({
  data,
  context,
}: ICellRendererParams<FinancialsRow>): React.JSX.Element | null => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");

  if (!data) return null;

  const ctx = context as FinancialsGridContext;
  const blockId = data.blockId ?? "default";
  const block = ctx.blockById.get(blockId);
  if (!block) return null;

  const { hidden, label } = block.creditsRow;
  const menuOpen = Boolean(anchorEl);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = (): void => setAnchorEl(null);

  const handleOpenEditDialog = (): void => {
    setLabelDraft(label);
    setEditDialogOpen(true);
    handleCloseMenu();
  };

  const handleHide = (): void => {
    ctx.queueChange(
      buildToggleCreditsHiddenDiff(blockId, hidden, ctx.payload.totalsBlocks),
    );
    handleCloseMenu();
  };

  const handleSaveLabel = (): void => {
    const trimmed = labelDraft.trim();
    if (trimmed) {
      ctx.queueChange(
        buildCreditsLabelDiff(blockId, trimmed, ctx.payload.totalsBlocks),
      );
    }
    setEditDialogOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      {label}
      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
        <MenuItem onClick={handleOpenEditDialog}>Edit Label</MenuItem>
        <MenuItem onClick={handleHide}>Hide Credits</MenuItem>
      </Menu>
      {editDialogOpen && (
        <Dialog
          open
          onClose={() => setEditDialogOpen(false)}
          title="Edit Credits Label"
          testIdSuffix="credits-label"
          actions={
            <>
              <Button
                onClick={() => setEditDialogOpen(false)}
                variant="tertiary"
              >
                Cancel
              </Button>
              <Button onClick={handleSaveLabel} variant="primary">
                Save
              </Button>
            </>
          }
        >
          <TextField
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            placeholder="Credits Label"
            fullWidth
          />
        </Dialog>
      )}
    </Box>
  );
};
